import React, { useEffect, useState } from 'react';
import { agentAuthHeaders } from '../utils/functionAuth';
import { getApiUrl } from '../utils/platform';
import { useChrome } from './ceo/ceoChrome';

async function postConsent(body) {
  const response = await fetch(getApiUrl('/.netlify/functions/support-consent'), {
    method: 'POST',
    headers: await agentAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Could not update support access');
  return data;
}

async function postRequest(body) {
  const response = await fetch(getApiUrl('/.netlify/functions/support-request'), {
    method: 'POST',
    headers: await agentAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Could not send the request');
  return data;
}

export default function SupportAccessSettings({ colors, preview }) {
  const chrome = useChrome(colors || {}, true);
  const [grant, setGrant] = useState(preview ? preview.grant : null);
  const [log, setLog] = useState(preview ? preview.log : []);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [kind, setKind] = useState('request');
  const [sent, setSent] = useState('');

  useEffect(() => {
    if (preview) return undefined;
    let live = true;
    postConsent({ action: 'status' })
      .then((data) => {
        if (!live) return;
        setGrant(data.grant || null);
        setLog(data.log || []);
      })
      .catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [preview]);

  async function toggle() {
    if (preview) {
      setGrant(grant ? null : preview.grant || { expires_at: new Date(Date.now() + 86400000).toISOString() });
      return;
    }
    setError('');
    try {
      const data = await postConsent({ action: grant ? 'revoke' : 'grant' });
      setGrant(data.grant || null);
      if (!grant) {
        const fresh = await postConsent({ action: 'status' });
        setLog(fresh.log || []);
      }
    } catch (err) {
      setError(err.message || 'Could not update support access');
    }
  }

  async function send(event) {
    event.preventDefault();
    if (preview) {
      setSent('Preview only. Nothing was sent.');
      return;
    }
    setError('');
    try {
      await postRequest({ kind, subject, body: message });
      setSubject('');
      setMessage('');
      setSent('Sent to Pacarion support.');
    } catch (err) {
      setError(err.message || 'Could not send the request');
    }
  }

  const expires = grant && grant.expires_at ? new Date(grant.expires_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <div data-testid="support-access" style={{ ...chrome.page, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <div style={chrome.panel}>
        <h3 style={{ ...chrome.title, fontSize: 22 }}>Support access</h3>
        <p style={chrome.sub}>Allow Pacarion support to view my account for 24 hours. You can turn this off at any time. It ends on its own.</p>
        <button type="button" onClick={toggle} style={{ ...chrome.button, marginTop: 16, background: grant ? '#b42318' : '#1d1d1f' }}>
          {grant ? 'Revoke access' : 'Allow for 24 hours'}
        </button>
        {grant ? <p style={chrome.sub}>Access is on until {expires}.</p> : null}
        {error ? <p style={{ color: '#b42318' }}>{error}</p> : null}
      </div>
      <div style={chrome.panel}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Access log</h3>
        {log.length === 0 ? <p style={chrome.sub}>Pacarion support has not viewed this account.</p> : null}
        {log.map((row) => (
          <p key={row.id || row.sentence} data-testid="support-access-log" style={{ margin: '8px 0', lineHeight: 1.4 }}>{row.sentence}</p>
        ))}
      </div>
      <form onSubmit={send} style={chrome.panel}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Contact support</h3>
        <p style={chrome.sub}>A request or a bug report. Do not include homeowner details.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <select aria-label="Kind" value={kind} onChange={(event) => setKind(event.target.value)} style={chrome.field}>
            <option value="request">Support request</option>
            <option value="bug">Bug report</option>
          </select>
          <input aria-label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Subject" style={chrome.field} />
          <textarea aria-label="Message" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="What happened" style={chrome.area} />
          <button type="submit" style={chrome.button} disabled={!subject.trim() || !message.trim()}>Send</button>
          {sent ? <p style={chrome.sub}>{sent}</p> : null}
        </div>
      </form>
    </div>
  );
}
